/**
 * DeadlineOverview — "Mijn deadlines" op /projects.
 *
 * Rustig herontwerp (25 sep 2026, zie list/deadlineLijst.js): elke rij toont zijn project
 * ("9252 · Full Frame — Algemene interne uren"), één rij per taak (oplevering leidend, start
 * als stille hint), verlopen alleen de laatste 30 dagen met de rest achter een klik, geen
 * pillen — één accentkleur, grijstinten, rood alleen voor "over tijd".
 * Klik op een taak opent het TaakPaneel; klik op een deliverable opent het project.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, ButtonBase, Collapse, Skeleton, Tooltip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { schrijfTaak } from '../../utils/planningBronnen';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { leesJson, meldFout } from '../../utils/apiFout';
import TaakPaneel from '../../ui-components/TaakPaneel/TaakPaneel';
import { klantVanProject } from './list/projectGezondheid';
import {
  VERLOPEN_GRENS, maakProjectIndex, namenPerProject, projectRegel, voegTakenSamen, telSoorten,
  filterSoort, splitsVerlopen, groepeerOpDatum, groepeerOpProject, isAfgevinkt, naarPaneelTaak,
} from './list/deadlineLijst';

const FONT = '"Inter", system-ui, sans-serif';
const TEKST = '#1f2937';
const GEDEMPT = '#6b7280';
const ZACHT = '#9ca3af';
const LIJN = '#eceef1';
const HOVER = '#f6f7f8';
const ACCENT = '#526075';
const ROOD = '#b91c1c';

const Icon = ({ name, size = 16, color = ZACHT }) => (
  <span className="material-symbols-outlined" aria-hidden style={{ fontSize: size, color, fontVariationSettings: "'FILL' 0, 'wght' 300" }}>{name}</span>
);

const SOORT = {
  taak: { icon: 'task_alt', woord: 'Taak' },
  deliverable: { icon: 'movie', woord: 'Deliverable' },
};

const kortDatum = (iso) => {
  if (!iso) return '';
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
};
const dagenTekst = (d) => {
  if (d < 0) return `${Math.abs(d)}d over`;
  if (d === 0) return 'vandaag';
  if (d === 1) return 'morgen';
  return `over ${d}d`;
};

/* ── Rij ─────────────────────────────────────────────────────────────────── */
function DeadlineRij({ item, regel, metProject, onKlik, onAfvinken }) {
  const s = SOORT[item.soort] || SOORT.taak;
  const verlopen = item.dagen < 0;
  const zwaar = item.deadlineType === 'Klant' || item.deadlineType === 'Hard';
  const opStart = item.opStart || item.alleenStart;
  const toonProject = metProject && !regel.leeg;
  const onder = [toonProject ? regel.tekst : null, item.assignee || null].filter(Boolean);
  return (
    <Box
      role="button" tabIndex={0} onClick={onKlik}
      onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onKlik(); } }}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1, cursor: 'pointer',
        borderBottom: `1px solid ${LIJN}`, '&:last-child': { borderBottom: 'none' },
        '&:hover, &:focus-visible': { bgcolor: HOVER, outline: 'none' },
        '&:hover .afvinken, &:focus-within .afvinken': { opacity: 1 },
      }}
    >
      <Tooltip title={s.woord} arrow placement="left">
        <Box sx={{ display: 'flex', flexShrink: 0 }}><Icon name={s.icon} /></Box>
      </Tooltip>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontFamily: FONT, fontSize: '0.82rem', fontWeight: 500, color: TEKST }}>{item.name}</Typography>
        {(onder.length > 0 || (metProject && regel.leeg)) && (
          <Tooltip title={toonProject ? regel.tekst : ''} enterDelay={500} placement="bottom-start">
            <Typography noWrap data-testid="projectregel" sx={{ fontFamily: FONT, fontSize: '0.72rem', color: GEDEMPT }}>
              {metProject && regel.leeg && <Box component="span" sx={{ color: ZACHT, fontStyle: 'italic' }}>geen project{item.assignee ? ' · ' : ''}</Box>}
              {onder.join(' · ')}
            </Typography>
          </Tooltip>
        )}
      </Box>
      {verlopen && item.soort === 'taak' && (
        <ButtonBase
          className="afvinken" onClick={(e) => { e.stopPropagation(); onAfvinken(item); }}
          sx={{ opacity: 0, transition: 'opacity .12s', fontFamily: FONT, fontSize: '0.72rem', color: ACCENT, px: 0.5, borderRadius: 1, '&:hover': { textDecoration: 'underline' } }}
        >afvinken?</ButtonBase>
      )}
      {item.startHint && (
        <Typography sx={{ fontFamily: FONT, fontSize: '0.72rem', color: ZACHT, flexShrink: 0, display: { xs: 'none', sm: 'block' } }}>start {kortDatum(item.startHint)}</Typography>
      )}
      <Typography sx={{
        fontFamily: FONT, fontSize: '0.72rem', flexShrink: 0, width: 44, textAlign: 'right', display: { xs: 'none', sm: 'block' },
        color: zwaar ? GEDEMPT : ZACHT, fontWeight: zwaar ? 500 : 400,
      }}>{item.soort === 'deliverable' || zwaar ? item.deadlineType || '' : ''}</Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: '0.75rem', color: GEDEMPT, flexShrink: 0, width: 88, textAlign: 'right' }}>
        {opStart && <Box component="span" sx={{ color: ZACHT }}>start </Box>}{kortDatum(item.datum)}
      </Typography>
      <Typography sx={{
        fontFamily: FONT, fontSize: '0.75rem', flexShrink: 0, width: 64, textAlign: 'right',
        color: verlopen ? ROOD : item.dagen === 0 ? ACCENT : ZACHT, fontWeight: item.dagen === 0 ? 600 : 400,
      }}>{dagenTekst(item.dagen)}</Typography>
    </Box>
  );
}

const Lijst = ({ children }) => (
  <Box sx={{ bgcolor: '#fff', border: `1px solid ${LIJN}`, borderRadius: '8px', overflow: 'hidden' }}>{children}</Box>
);

const Kop = ({ label, aantal }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, px: 0.5, mb: 0.75 }}>
    <Typography sx={{ fontFamily: FONT, fontSize: '0.72rem', fontWeight: 600, color: GEDEMPT, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Typography>
    {aantal > 0 && <Typography component="span" sx={{ fontFamily: FONT, fontSize: '0.72rem', color: ZACHT }}>{aantal}</Typography>}
  </Box>
);

function OudToggle({ aantal, open, onClick }) {
  if (!aantal) return null;
  return (
    <ButtonBase onClick={onClick} sx={{ mt: 0.75, px: 0.5, py: 0.25, borderRadius: 1, fontFamily: FONT, fontSize: '0.75rem', color: GEDEMPT, '&:hover': { color: TEKST } }}>
      {open ? `Verberg verlopen ouder dan ${VERLOPEN_GRENS} dagen` : `Ouder dan ${VERLOPEN_GRENS} dagen (${aantal}) ›`}
    </ButtonBase>
  );
}

/* ── Per project ─────────────────────────────────────────────────────────── */
function ProjectBlok({ groep, open, onToggle, rijProps }) {
  const { regel, items } = groep;
  return (
    <Box sx={{ mb: 2.5 }}>
      <ButtonBase onClick={onToggle} sx={{ width: '100%', justifyContent: 'flex-start', gap: 1, px: 0.5, py: 0.5, mb: 0.5, borderRadius: 1, textAlign: 'left' }}>
        <Icon name={open ? 'expand_more' : 'chevron_right'} size={18} />
        <Tooltip title={regel.leeg ? '' : regel.tekst} enterDelay={500} placement="bottom-start">
          <Typography noWrap sx={{ flex: 1, minWidth: 0, fontFamily: FONT, fontSize: '0.85rem', color: regel.leeg ? ZACHT : TEKST, fontStyle: regel.leeg ? 'italic' : 'normal' }}>
            {regel.nummer && <Box component="span" sx={{ fontWeight: 700 }}>{regel.nummer}</Box>}
            {regel.nummer && (regel.klant || regel.naam) && <Box component="span" sx={{ color: ZACHT }}> · </Box>}
            {regel.klant && <Box component="span" sx={{ color: GEDEMPT }}>{regel.klant}</Box>}
            {regel.klant && regel.naam && <Box component="span" sx={{ color: ZACHT }}> — </Box>}
            {regel.naam && <Box component="span" sx={{ fontWeight: 600 }}>{regel.naam}</Box>}
            {regel.leeg && 'geen project'}
          </Typography>
        </Tooltip>
        <Typography component="span" sx={{ fontFamily: FONT, fontSize: '0.75rem', color: ZACHT, flexShrink: 0 }}>{items.length}</Typography>
      </ButtonBase>
      <Collapse in={open}>
        <Lijst>{items.map((i) => <DeadlineRij key={i.id} item={i} regel={regel} metProject={false} {...rijProps(i)} />)}</Lijst>
      </Collapse>
    </Box>
  );
}

function DeadlinesSkeleton() {
  return (
    <Box>
      {[1, 2].map((g) => (
        <Box key={g} sx={{ mb: 3 }}>
          <Skeleton variant="text" width={120} height={18} />
          <Lijst>
            {[1, 2, 3].map((r) => (
              <Box key={r} sx={{ display: 'flex', gap: 1.5, p: 1.25, borderBottom: `1px solid ${LIJN}` }}>
                <Skeleton variant="circular" width={16} height={16} />
                <Box sx={{ flex: 1 }}><Skeleton variant="text" width="40%" height={16} /><Skeleton variant="text" width="60%" height={14} /></Box>
                <Skeleton variant="text" width={60} height={16} />
              </Box>
            ))}
          </Lijst>
        </Box>
      ))}
    </Box>
  );
}

/** Stille tekstknop voor weergave en filter: actief = donker met accentstreep, verder grijs. */
function Keuze({ actief, onClick, children }) {
  return (
    <ButtonBase onClick={onClick} aria-pressed={actief} sx={{
      fontFamily: FONT, fontSize: '0.78rem', px: 0.75, py: 0.5, borderRadius: 1,
      color: actief ? TEKST : GEDEMPT, fontWeight: actief ? 600 : 400,
      boxShadow: actief ? `inset 0 -2px 0 ${ACCENT}` : 'none', '&:hover': { color: TEKST },
    }}>{children}</ButtonBase>
  );
}

const FILTERS = [['alles', 'Alles'], ['taak', 'Taken'], ['deliverable', 'Deliverables'], ['start', 'Start binnenkort']];

/* ── Main ────────────────────────────────────────────────────────────────── */
function DeadlineOverview({ userEmail, projecten }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ byDate: [] });
  const [isCached, setIsCached] = useState(false);
  const [groupMode, setGroupMode] = useState('date');
  const [soort, setSoort] = useState('alles');
  const [toonOud, setToonOud] = useState(false);
  const [dicht, setDicht] = useState({});
  const [gekozen, setGekozen] = useState(null);
  const [lokaal, setLokaal] = useState({}); // taak-id → velden na een schrijfactie
  const [vuil, setVuil] = useState(false);

  const fetchDeadlines = useCallback(async (forceRefresh = false) => {
    if (!userEmail) return;
    if (forceRefresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/my-deadlines?email=${encodeURIComponent(userEmail)}&includeStart=true${forceRefresh ? '&refresh=true' : ''}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setData(d);
      setIsCached(d.cached === true);
      setLokaal({});
    } catch (err) { setError(`Kon deadlines niet laden: ${err.message}`); }
    finally { setLoading(false); setRefreshing(false); }
  }, [userEmail]);

  useEffect(() => { fetchDeadlines(); }, [fetchDeadlines]);

  const opties = useMemo(() => ({
    index: maakProjectIndex(projecten || [], klantVanProject),
    namen: namenPerProject(data.byDate || []),
  }), [projecten, data.byDate]);

  const samen = useMemo(
    () => voegTakenSamen(data.byDate || []).filter((i) => !(i.soort === 'taak' && isAfgevinkt(lokaal[i.id]))),
    [data.byDate, lokaal],
  );
  const tellers = useMemo(() => telSoorten(samen), [samen]);
  const zichtbaar = useMemo(() => filterSoort(samen, soort), [samen, soort]);
  const { recent, oud } = useMemo(() => splitsVerlopen(zichtbaar), [zichtbaar]);
  const getoond = toonOud ? zichtbaar : recent;
  const datumGroepen = useMemo(() => groepeerOpDatum(getoond), [getoond]);
  const projectGroepen = useMemo(() => groepeerOpProject(getoond, opties), [getoond, opties]);

  const bijgewerkt = useCallback((id, velden) => {
    setLokaal((l) => ({ ...l, [id]: { ...l[id], ...velden } }));
    setVuil(true);
  }, []);

  // "afvinken?" bij verlopen taken: direct afvinken, met ongedaan maken (utils/ongedaan).
  const afvinken = useCallback(async (item) => {
    const url = `${BACKEND_URL}/Api/tasks/${encodeURIComponent(item.id)}`;
    const zet = async (velden) => {
      const r = await schrijfTaak(url, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(velden) });
      await leesJson(r, 'Taak afvinken');
      bijgewerkt(item.id, velden);
    };
    try {
      await zet({ Afgerond: true, PrioNr: 'Afgerond' });
      const terugStatus = item.status && !isAfgevinkt({ PrioNr: item.status }) ? item.status : 'Geen prio';
      biedOngedaanAan(`${item.name} afgevinkt`, () => zet({ Afgerond: false, PrioNr: terugStatus }));
    } catch (e) { meldFout(e, 'Taak afvinken'); }
  }, [bijgewerkt]);

  const klik = useCallback((item) => {
    if (item.soort === 'taak') setGekozen(item);
    else if (item.projectId) navigate(`/projects/${item.projectId}`);
  }, [navigate]);

  const sluitPaneel = useCallback(() => {
    setGekozen(null);
    if (vuil) { setVuil(false); fetchDeadlines(true); }
  }, [vuil, fetchDeadlines]);

  const paneelTaak = useMemo(() => (gekozen ? naarPaneelTaak(gekozen) : null), [gekozen]);
  const rijProps = (item) => ({ onKlik: () => klik(item), onAfvinken: afvinken });
  const wisselOud = () => setToonOud((v) => !v);

  if (error) {
    return <Typography sx={{ fontFamily: FONT, fontSize: '0.85rem', color: ROOD, py: 4, textAlign: 'center' }}>{error}</Typography>;
  }

  const leeg = !loading && samen.length === 0;

  return (
    <Box>
      {/* Bediening: weergave links, soort-filter midden, verversen rechts — alles als stille tekst. */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Keuze actief={groupMode === 'date'} onClick={() => setGroupMode('date')}>Op deadline</Keuze>
          <Keuze actief={groupMode === 'project'} onClick={() => setGroupMode('project')}>Per project</Keuze>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap' }}>
          {FILTERS.map(([k, label]) => (
            <Keuze key={k} actief={soort === k} onClick={() => setSoort(k)}>
              {label}<Box component="span" sx={{ color: ZACHT, fontWeight: 400, ml: 0.75 }}>{tellers[k]}</Box>
            </Keuze>
          ))}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isCached && <Typography sx={{ fontFamily: FONT, fontSize: '0.72rem', color: ZACHT }}>uit cache</Typography>}
          <Tooltip title="Verversen" arrow>
            <ButtonBase onClick={() => !refreshing && fetchDeadlines(true)} aria-label="Verversen" sx={{ width: 28, height: 28, borderRadius: 1, '&:hover': { bgcolor: HOVER } }}>
              {refreshing
                ? <Box sx={{ width: 14, height: 14, border: `2px solid ${LIJN}`, borderTop: `2px solid ${ACCENT}`, borderRadius: '50%', animation: 'spin .8s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
                : <Icon name="refresh" size={18} />}
            </ButtonBase>
          </Tooltip>
        </Box>
      </Box>

      {loading && <DeadlinesSkeleton />}

      {leeg && (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: FONT, fontSize: '0.9rem', fontWeight: 600, color: GEDEMPT, mb: 0.5 }}>Geen deadlines gevonden</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: '0.8rem', color: ZACHT }}>Er zijn geen openstaande deadlines voor jouw projecten</Typography>
        </Box>
      )}

      {!loading && !leeg && zichtbaar.length === 0 && (
        <Typography sx={{ fontFamily: FONT, fontSize: '0.82rem', color: ZACHT, py: 4, textAlign: 'center' }}>Niets in deze selectie</Typography>
      )}

      {!loading && groupMode === 'date' && (
        <Box>
          {datumGroepen.map((g) => (
            <Box key={g.sleutel} sx={{ mb: 3 }}>
              <Kop label={g.label} aantal={g.items.length} />
              <Lijst>{g.items.map((i) => <DeadlineRij key={i.id} item={i} regel={projectRegel(i, opties)} metProject {...rijProps(i)} />)}</Lijst>
              {g.sleutel === 'verlopen' && <OudToggle aantal={oud.length} open={toonOud} onClick={wisselOud} />}
            </Box>
          ))}
          {!datumGroepen.some((g) => g.sleutel === 'verlopen') && oud.length > 0 && (
            <Box sx={{ mb: 3 }}><Kop label="Verlopen" aantal={0} /><OudToggle aantal={oud.length} open={toonOud} onClick={wisselOud} /></Box>
          )}
        </Box>
      )}

      {!loading && groupMode === 'project' && (
        <Box>
          {projectGroepen.map((g) => (
            <ProjectBlok key={g.projectId} groep={g} open={!dicht[g.projectId]} onToggle={() => setDicht((d) => ({ ...d, [g.projectId]: !d[g.projectId] }))} rijProps={rijProps} />
          ))}
          <OudToggle aantal={oud.length} open={toonOud} onClick={wisselOud} />
        </Box>
      )}

      <TaakPaneel
        taak={paneelTaak}
        open={Boolean(gekozen)}
        onClose={sluitPaneel}
        onBijgewerkt={bijgewerkt}
        onVerwijderd={(id) => { bijgewerkt(id, { Afgerond: true }); setGekozen(null); }}
        onNaarProject={(pid) => navigate(`/projects/${pid}`)}
      />
    </Box>
  );
}

export default DeadlineOverview;
